import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Divider,
  Input,
  InputAdornment,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";

import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import { SearchIcon, LockPersonIcon } from "../../Resources/Icons/icons";
import { GetClientCompanyWallet } from "../../Service/_index";
import { WALLET_INSURERS, checkUserType } from "../../Shared/CommonConstant";
import ClientWalletDetail from "./ClientWalletDetail";
import Styles from "./ClientCompanyWallet.module.scss";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ClientCompanyWalletList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const GetData = useCallback(() => {
    setLoading(true);
    GetClientCompanyWallet()
      .then((res) => setRows(res?.data?.rows || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    GetData();
  }, [GetData]);

  const goToDetail = (clientId) => navigate(`/clientCompanyWallet/${clientId}`);

  // A SUPER_ADMIN login sees every client here (non-SUPER_ADMIN callers only
  // ever get their own row from the backend). Pin the logged-in client's own
  // row to the top so they land on their own wallet first instead of hunting
  // for it alphabetically among everyone else's.
  const ownClientId = sessionStorage.getItem("clientID");

  const filteredRows = useMemo(() => {
    const filtered = rows.filter((r) =>
      (r.clientName || "").toLowerCase().includes(searchText.toLowerCase())
    );
    const own = filtered.filter((r) => String(r.clientId) === String(ownClientId));
    const others = filtered.filter((r) => String(r.clientId) !== String(ownClientId));
    return [...own, ...others].map((r, i) => ({ ...r, __index__: i + 1 }));
  }, [rows, searchText, ownClientId]);

  const MoneyCell = ({ value, strong, onClick }) => (
    <Typography
      onClick={onClick}
      className={Styles.ListMoneyCell}
      sx={{
        fontSize: 13,
        fontWeight: strong ? 700 : 500,
        color: strong ? "#101828" : Number(value) > 0 ? "#1E3A5F" : "#98A2B3",
      }}
    >
      {money(value)}
    </Typography>
  );

  const columns = [
    { id: "__index__", label: "#", type: "index" },
    {
      id: "clientName",
      label: "Client",
      type: "component",
      renderComponent: (row) => (
        <Typography className={Styles.ClientNameLink} onClick={() => goToDetail(row.clientId)}>
          {row.clientName}
        </Typography>
      ),
    },
    ...WALLET_INSURERS.map((company) => ({
      id: company,
      label: company,
      type: "component",
      renderComponent: (row) => (
        <Tooltip title={`View ${company} wallet for ${row.clientName}`} placement="top">
          <span style={{ width: "100%", display: "inline-block" }}>
            <MoneyCell value={row.balances?.[company]} onClick={() => goToDetail(row.clientId)} />
          </span>
        </Tooltip>
      ),
    })),
    {
      id: "total",
      label: "Total",
      type: "component",
      renderComponent: (row) => <MoneyCell value={row.total} strong onClick={() => goToDetail(row.clientId)} />,
    },
    {
      id: "action",
      label: "Action",
      type: "component",
      renderComponent: (row) => (
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={() => goToDetail(row.clientId)}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: "6px",
            backgroundColor: "#1E3A5F",
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#2E5A8F" },
          }}
        >
          View Wallet
        </Button>
      ),
    },
  ];

  return (
    <Grid container className="Master_Card_Container">
      <Grid container alignItems="center" className="Master_Card_Sub_Container">
        <Grid item xs={12} sm={6}>
          <Typography className="Master_Card_Tittle">Company Wallet</Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
      </Grid>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        className="Master_Card_Sub_Container"
      >
        <Grid item xs={12} sm={6} />
        <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
          <Input
            className="w-100 Master_Card_Input"
            disableUnderline
            placeholder="Search Client"
            onChange={(e) => setSearchText(e.target.value)}
            startAdornment={
              <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                <IconButton aria-label="search">
                  <SearchIcon sx={{ fontSize: "20px" }} />
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>
      </Grid>
      <MasterCommonTable
        tableConfig={{ isAction: false, isView: false, isEdit: false, isDelete: false, isSwitch: false }}
        data={filteredRows}
        columns={columns}
        tableTotalCount={filteredRows.length}
        loading={loading}
        // Searching shrinks the list: start again from page 1 so the first matches are never hidden on a later page.
        paginationReset={searchText}
      />
    </Grid>
  );
};

const AccessDenied = () => {
  const navigate = useNavigate();
  return (
    <Grid container className="Master_Card_Container">
      <Grid container alignItems="center" className="Master_Card_Sub_Container">
        <Typography className="Master_Card_Tittle">Company Wallet</Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
      </Grid>
      <Grid item xs={12} className={Styles.AccessDenied}>
        <LockPersonIcon sx={{ fontSize: 48, color: "#98A2B3" }} />
        <Typography sx={{ mt: 2, fontSize: 16, fontWeight: 600, color: "#101828" }}>
          You do not have access to this page
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 13, color: "#667085" }}>
          Company wallet balances are available to admin (CLIENT) logins only.
        </Typography>
        <Button className="Master_Card_create_Button" sx={{ mt: 3 }} onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </Button>
      </Grid>
    </Grid>
  );
};

const ClientCompanyWallet = () => {
  const { clientId } = useParams();
  const { isClient } = checkUserType(sessionStorage.getItem("userType"));
  const isSuperAdmin = sessionStorage.getItem("roleId") === "SUPER_ADMIN";

  if (!isClient) {
    return <AccessDenied />;
  }

  if (clientId) {
    return <ClientWalletDetail clientId={clientId} />;
  }

  // Only a SUPER_ADMIN login sees every client's wallet (the backend already
  // scopes a non-SUPER_ADMIN caller to just their own row) — so for everyone
  // else the list is always exactly one row. Skip it and go straight to their
  // own wallet instead of showing a table with a single entry to click.
  if (!isSuperAdmin) {
    return <ClientWalletDetail clientId={sessionStorage.getItem("clientID")} showBackLink={false} />;
  }

  return <ClientCompanyWalletList />;
};

export default ClientCompanyWallet;
